/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import type { GameState } from '../types';
import { UI_THROTTLE } from '../config/constants';

export class Store {
  private state: GameState;
  private listeners: (() => void)[] = [];
  private pendingNotify = false;
  private lastNotifyPoints = 0;
  private notificationIntervalId: number | undefined;

  constructor(initialState: GameState) {
    this.state = { ...initialState };

    // Set up periodic notification for throttled updates
    this.notificationIntervalId = window.setInterval(() => {
      if (this.pendingNotify) {
        // Always notify if points changed significantly (>5% or when starting from 0)
        const pointDiff = Math.abs(this.state.points - this.lastNotifyPoints);
        const threshold =
          this.lastNotifyPoints === 0 ? 0 : this.lastNotifyPoints * 0.05;
        const shouldNotify = pointDiff > threshold;

        if (shouldNotify || this.pendingNotify) {
          this.notifyListeners();
          this.lastNotifyPoints = this.state.points;
          this.pendingNotify = false;
        }
      }
    }, UI_THROTTLE.STORE_NOTIFICATION_INTERVAL);
  }

  /**
   * Clean up resources (intervals, listeners)
   * Call this when the Store instance is no longer needed
   */
  destroy(): void {
    if (this.notificationIntervalId !== undefined) {
      clearInterval(this.notificationIntervalId);
      this.notificationIntervalId = undefined;
    }
    this.listeners = [];
  }

  getState(): GameState {
    return { ...this.state };
  }

  /**
   * Get state reference without cloning (for performance in hot loops)
   * WARNING: Do not mutate the returned state directly!
   */
  getReadonlyState(): Readonly<GameState> {
    return this.state;
  }

  setState(updates: Partial<GameState>): void {
    this.state = { ...this.state, ...updates };
    this.notifyListeners();
  }

  addPoints(amount: number): void {
    this.state.points += amount;
    this.state.stats.totalDamage += amount;
    // Mark that we need to notify, but don't do it immediately (throttled by interval)
    this.pendingNotify = true;
  }

  spendPoints(amount: number): boolean {
    if (this.state.points >= amount) {
      this.state.points -= amount;
      this.notifyListeners();
      return true;
    }
    return false;
  }

  incrementClick(): void {
    this.state.stats.totalClicks++;
  }

  incrementAlienKill(): void {
    this.state.stats.aliensKilled++;
  }

  incrementBossKill(): void {
    this.state.stats.bossesKilled++;
  }

  incrementUpgrade(): void {
    this.state.stats.totalUpgrades++;
  }

  incrementSubUpgrade(): void {
    this.state.stats.totalSubUpgrades++;
  }

  updateMaxLevel(): void {
    if (this.state.level > this.state.stats.maxLevel) {
      this.state.stats.maxLevel = this.state.level;
    }
  }

  addPlayTime(seconds: number): void {
    this.state.stats.playTime += seconds;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => {
      listener();
    });
  }
}
